import { useMutation } from '@apollo/client';
import React, { useState } from 'react';

import {
  HStack,
  Heading,
  Input,
  NewButton,
  Paragraph,
  VStack,
  useToast,
} from '@eluve/components';
import { useAssignedTenantIdFromParams } from '@eluve/session-helpers';
import {
  useTenantLogo,
  useTenantName,
  useUploadGenericFile,
} from '@eluve/smart-blocks';

import { updateTenantDetailsMutation } from './operations';

export const TenantLogoSettings: React.FC = () => {
  const tenantId = useAssignedTenantIdFromParams();
  const { tenantLogoUrl } = useTenantLogo(tenantId);
  const tenantName = useTenantName(tenantId);
  const [isUploading, setIsUploading] = useState(false);
  const { uploadGenericFileToGCS } = useUploadGenericFile(tenantId);
  const toast = useToast();

  const [updateTenantDetails] = useMutation(updateTenantDetailsMutation, {
    onError: () => {
      toast.error('Failed to update organization logo');
    },
  });

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.currentTarget.files?.[0];
    if (!file) return;

    try {
      setIsUploading(true);
      const fileExt = file.name.split('.').pop();

      if (!fileExt) {
        toast.error('Invalid file extension.');
        return;
      }

      const result = await uploadGenericFileToGCS({
        file,
        mimeType: 'image/*',
        fileExt,
      });

      if (result.url) {
        await updateTenantDetails({
          variables: {
            tenantId,
            logoUrl: result.url,
            name: tenantName,
          },
        });
      }
    } catch {
      toast.error('Failed to update organization logo.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setIsUploading(true);
      await updateTenantDetails({
        variables: {
          tenantId,
          logoUrl: '',
          name: tenantName,
        },
      });
    } catch {
      toast.error('Failed to delete organization logo.');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <VStack gap={4}>
      <VStack>
        <Heading size="md">Organization Logo</Heading>
        <Paragraph color="secondary" size="sm">
          Upload a logo for your organization. This will be displayed in various
          parts of the application.
        </Paragraph>
      </VStack>
      {tenantLogoUrl ? (
        <HStack
          className="rounded-lg border border-border-primary bg-bg-primary p-4"
          justify="between"
        >
          <img
            src={tenantLogoUrl}
            alt="Organization logo"
            className="max-h-14"
          />
          <NewButton
            disabled={isUploading}
            icon={{ name: 'Trash' }}
            onClick={handleDelete}
            type="outline"
            size="m"
          />
        </HStack>
      ) : (
        <Input
          type="file"
          accept="image/*"
          className="w-72 rounded-lg border border-border-primary bg-bg-primary"
          disabled={isUploading}
          onChange={handleFileChange}
        />
      )}
    </VStack>
  );
};
